/**
 * Detect "stale bundle" errors — the runtime fallout of a deploy that renamed or
 * re-split JS chunks while a user still had old HTML / an old bundle in memory.
 * The consuming chunk then calls into a sibling that no longer exports the symbol
 * it expects ("a.useLocaleStore is not a function", prod 2026-06-18), or a lazily
 * imported chunk 404s.
 *
 * The error boundary's reset() cannot fix these — the broken modules are already
 * evaluated in memory; only a full page reload fetches fresh HTML + chunks. So
 * callers should hard-reload ONCE (sessionStorage-guarded against loops) and
 * report the error first, so a genuine bug that happens to match still surfaces
 * on the second occurrence instead of being silently swallowed.
 */
export function isStaleBundleError(
  error: { name?: string; message?: string } | null | undefined,
): boolean {
  if (!error) return false;
  const name = error.name || "";
  const msg = error.message || "";
  return (
    name === "ChunkLoadError" ||
    /Loading chunk [\w-]+ failed/i.test(msg) ||
    // Turbopack (Next 16) : "Failed to load chunk /_next/static/chunks/… from
    // module N" (prod 2026-07-11, rafale post-déploiement sur 3 pages).
    /Failed to load chunk/i.test(msg) ||
    /Loading CSS chunk/i.test(msg) ||
    /Failed to fetch dynamically imported module/i.test(msg) ||
    /error loading dynamically imported module/i.test(msg) ||
    // Module-shape mismatch after a chunk re-split: a sibling module no longer
    // exports the binding the caller imported (e.g. "x.useLocaleStore is not a
    // function"). Ambiguous with real bugs, but the one-shot guard + report-first
    // means a true bug still surfaces on its second occurrence.
    /\bis not a function\b/.test(msg) ||
    // Storage SecurityError ("Failed to read the 'localStorage' property from
    // 'Window': Access is denied for this document", prod 2026-06-23). Current
    // source guards every localStorage/sessionStorage access (see lib/safeStorage),
    // so this can only reach the boundary from a STALE bundle whose old chunk still
    // has an unguarded access — the one-shot reload fetches the fresh, guarded
    // bundle and renders cleanly. On a genuinely storage-blocked browser the reload
    // guard fails safe (sessionStorage.getItem throws → no reload → crash UI, no
    // loop), so matching here costs at most one harmless reload.
    /read the '(local|session)Storage' property/i.test(msg) ||
    /Access is denied for this document/i.test(msg)
  );
}


/**
 * Garde anti-boucle de l'auto-reload : autorise UN reload par fenêtre de 2 min
 * (au lieu d'un par session — on déploie plusieurs fois par jour, un onglet
 * ouvert peut subir plusieurs chunk-skews dans la même session). Si le reload
 * n'a pas guéri (récidive < 2 min), on rend la main au boundary : crash UI +
 * rapport Discord — un vrai bug refait donc surface immédiatement.
 */
export function shouldAutoReloadStaleBundle(): boolean {
  try {
    const key = "velyq-stale-bundle-reloaded-at";
    const last = Number(sessionStorage.getItem(key) || "0");
    const now = Date.now();
    if (now - last < 120_000) return false;
    sessionStorage.setItem(key, String(now));
    return true;
  } catch {
    return false; // storage indisponible — pas de reload, crash UI (pas de boucle)
  }
}
